import moment from 'moment';
import { roundMoney } from './moneyFormat';

export const EMPTY_CUSTOMER_COMPANY_ID = '00000000-0000-0000-0000-000000000000';

export const BOTMARK_INVOICE_ITEM_NAME = {
    Credit: 'Credit',
    BmPoints: 'BM Points',
    ClPoints: 'CL Points',
    BaseServiceFee: 'Botmark Base Service Fee',
};

/** True when a credit references a real plan, not a cleared or empty selection. */
export function hasEstimateReferencePlan(selectedFile) {
    return Boolean(selectedFile?.fileId)
        && selectedFile.fileId !== EMPTY_CUSTOMER_COMPANY_ID;
}

export function isInternalEstimateCustomer(selectedCustomer) {
    return selectedCustomer?.userCompanyId === EMPTY_CUSTOMER_COMPANY_ID
        || selectedCustomer?.userCompanyId == null;
}

/** Per-point rate for the company that receives the invoice. No customer means the processor. */
export function getInvoiceChargedRate({ processorCompany, selectedCustomer }) {
    if (isInternalEstimateCustomer(selectedCustomer)) {
        return processorCompany?.companyRate;
    }

    return selectedCustomer?.companyRate;
}

function buildProcessorInfo(variableCompanyData, userData) {
    return {
        name: variableCompanyData?.name,
        street: variableCompanyData?.street,
        city: variableCompanyData?.city,
        state: variableCompanyData?.state,
        zip: variableCompanyData?.zip,
        country: variableCompanyData?.country,
        email: variableCompanyData?.email,
        companyStatus: variableCompanyData?.companyStatus,
        phoneNumber: variableCompanyData?.phoneNumber,
        processorName: `${userData?.firstName} ${userData?.lastName}`,
    };
}

function buildCreditLineItem({ selectedFile, referenceData, creditReason, planCredit }) {
    const hasReferenceFile = hasEstimateReferencePlan(selectedFile);
    const description = hasReferenceFile
        ? `${selectedFile.fileName}, # ${referenceData.estimateNumber}, ${referenceData.date}: ${creditReason}`
        : creditReason;
    return {
        itemName: BOTMARK_INVOICE_ITEM_NAME.Credit,
        description,
        unit: 'special',
        rate: -planCredit,
        quantity: 1,
        total: -planCredit,
    };
}

function buildPointLineItem(itemName, description, rate, quantity) {
    return {
        itemName,
        description,
        unit: 'per/point',
        rate,
        quantity,
        total: rate * quantity,
    };
}

function buildBaseServiceFeeLineItem(baseServiceFee) {
    const fee = Number(baseServiceFee);
    return {
        itemName: BOTMARK_INVOICE_ITEM_NAME.BaseServiceFee,
        description: 'Manual Processing',
        unit: '',
        rate: fee,
        quantity: 1,
        total: fee,
    };
}

function buildInvoiceShell({
    estimateData,
    items,
    planCredit,
    selectedFile,
    finalTotal,
    pointTypeTotal,
    selectedCustomer,
    variableCompanyData,
    userData,
    botmarkInfo,
}) {
    const hasReferenceFile = hasEstimateReferencePlan(selectedFile);

    return {
        date: moment().format('MM/DD/YYYY'),
        estimateNumber: estimateData.estimateNumber,
        items,
        taxes: [],
        creditAdjustment: planCredit,
        relatedFile: hasReferenceFile ? selectedFile : null,
        finalTotal,
        pointTypeTotal,
        customerInfo: selectedCustomer,
        processorInfo: buildProcessorInfo(variableCompanyData, userData),
        botmarkInfo,
    };
}

/** Builds the invoice payload stored when a Botmark estimate is accepted. */
export function buildBotmarkEstimateInvoice({
    estimateData,
    isProcessed,
    specialPointTypeTotal,
    pointTypeTotal,
    planCredit,
    selectedFile,
    referenceData,
    creditReason,
    baseServiceFee,
    selectedCustomer,
    variableCompanyData,
    userData,
    botmarkInfo,
}) {
    const creditLineItemArgs = {
        selectedFile,
        referenceData,
        creditReason,
        planCredit,
    };
    const hasCredit = planCredit != 0;
    const items = [];

    if (isProcessed == 1) {
        const chargedRate = getInvoiceChargedRate({
            processorCompany: variableCompanyData,
            selectedCustomer,
        });

        items.push(buildPointLineItem(BOTMARK_INVOICE_ITEM_NAME.BmPoints, 'E+C+U', chargedRate, pointTypeTotal));
        items.push(buildPointLineItem(
            BOTMARK_INVOICE_ITEM_NAME.ClPoints,
            'Chargeable Special Points',
            chargedRate,
            specialPointTypeTotal,
        ));
        if (hasCredit) {
            items.push(buildCreditLineItem(creditLineItemArgs));
        }

        const pointsTotal = chargedRate * (pointTypeTotal + specialPointTypeTotal);

        return buildInvoiceShell({
            estimateData,
            items,
            planCredit,
            selectedFile,
            finalTotal: roundMoney(pointsTotal - planCredit),
            pointTypeTotal,
            selectedCustomer,
            variableCompanyData,
            userData,
            botmarkInfo,
        });
    }

    items.push(buildBaseServiceFeeLineItem(baseServiceFee));
    if (hasCredit) {
        items.push(buildCreditLineItem(creditLineItemArgs));
    }

    return buildInvoiceShell({
        estimateData,
        items,
        planCredit,
        selectedFile,
        finalTotal: roundMoney(Number(baseServiceFee) - planCredit),
        pointTypeTotal,
        selectedCustomer,
        variableCompanyData,
        userData,
        botmarkInfo,
    });
}
